import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Vehicle, VehicleAnalyticsStats } from '@/types/vehicle';
import { DirectoryItemChart, type PeriodOption } from './DirectoryItemChart';
import { DirectoryItemInfoPanel } from './DirectoryItemInfoPanel';
import { EngineService } from '@/services/engine';
import { buildEntityAnalyticsFields } from '../entityAnalyticsFields';
import { getVehicleStatsCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { useChartPointNavigation } from '@/hooks/useChartPointNavigation';
import { PERIOD_CONFIG, getStartTime } from '@/utils/chart-periods';
import type { ChartDataPoint } from '@/components/Chart';

interface VehicleOverviewTabProps {
    vehicle: Vehicle;
}

export function VehicleOverviewTab({ vehicle }: VehicleOverviewTabProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const navigate = useNavigate();
    const navigateToChartRange = useChartPointNavigation();
    const [activePeriod, setActivePeriod] = useState<PeriodOption>('7days');

    const config = PERIOD_CONFIG[activePeriod];
    const groupBy = config.groupBy;
    const startTime = getStartTime(config.milliseconds, groupBy);

    const { data: stats, isLoading: statsLoading } =
        useSWR<VehicleAnalyticsStats>(
            vehicle.id
                ? getVehicleStatsCacheKey(vehicle.id, activePeriod)
                : null,
            () =>
                EngineService.getVehicleStats(vehicle.id, {
                    startTime,
                    groupBy,
                }),
            { keepPreviousData: true },
        );

    const handleSourceClick = useCallback(
        (source: { id: string }) => {
            const params = new URLSearchParams();
            params.set('vehicleId', vehicle.id);
            params.set('sources', source.id);
            navigate(`${AppRoutes.EVENTS}?${params.toString()}`);
        },
        [navigate, vehicle.id],
    );

    const handlePointClick = useCallback(
        (_dataPoint: ChartDataPoint, index: number) => {
            const point = stats?.chartData[index];
            if (!point) return;
            navigateToChartRange(
                AppRoutes.EVENTS,
                { vehicleId: vehicle.id },
                point.startTime,
                point.endTime,
            );
        },
        [navigateToChartRange, vehicle.id, stats],
    );

    const fields = buildEntityAnalyticsFields(t, {
        firstSeen: vehicle.createdAt,
        lastSeen: vehicle.lastSeen,
        totalEvents: stats?.totalEventsOverall ?? 0,
        totalSources: stats?.totalSources ?? 0,
    });

    return (
        <div className="flex flex-col gap-6 pb-24">
            <div className="flex flex-col border-b border-grey-500 @wide:flex-row text-sm">
                <DirectoryItemInfoPanel fields={fields} />
            </div>

            <DirectoryItemChart
                chartData={stats?.chartData ?? []}
                totalEvents={stats?.totalEvents ?? 0}
                sourceStats={stats?.sourceStats ?? []}
                isLoading={statsLoading}
                activePeriod={activePeriod}
                onPeriodChange={setActivePeriod}
                onPointClick={handlePointClick}
                onSourceClick={handleSourceClick}
            />
        </div>
    );
}
